import React, { useEffect, useMemo, useState } from 'react';
import { DeleteOutlined } from '@ant-design/icons';
import { Button, Flex, Form, FormInstance, Input, Select, Typography, theme } from 'antd';
import { getStaffList } from '@/services/staff';
import { getTeacherList } from '@/services/teacher';
import { EmploymentStatus } from '@/types/common';
import { TeacherInfo } from '@/types/teacher';
import {
  calculatePerformanceOwnershipAmountInCent,
  getPerformanceOwnershipRatioTotal,
  syncDefaultPerformanceOwnershipItems,
  validatePerformanceOwnershipItems,
} from './utils';
import {
  PerformanceDefaultOwner,
  PerformanceOwnerOption,
  PerformanceOwnershipFormItem,
} from './types';
import { PerformanceRoleType } from '@/types/staff';

interface IProps {
  title: string;
  description?: string;
  form: FormInstance;
  name?: string;
  storeId?: number;
  totalAmount?: number | string;
  defaultOwner?: PerformanceDefaultOwner;
  lockDefaultOwner?: boolean;
}

const sectionStyle: React.CSSProperties = {
  marginTop: 24,
  padding: 24,
  borderRadius: 20,
  background: 'rgba(248, 250, 255, 0.9)',
  border: '1px solid rgba(23, 32, 51, 0.06)',
};

const cardStyle: React.CSSProperties = {
  padding: 18,
  borderRadius: 20,
  background: '#fff',
  border: '1px solid #f0f2f7',
  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.9)',
};

const summaryStyle: React.CSSProperties = {
  marginTop: 16,
  padding: '16px 20px',
  borderRadius: 18,
  background: '#edf4ff',
  color: '#1f2937',
  fontWeight: 500,
};

const roleOptions = [
  { label: '后台员工', value: PerformanceRoleType.Staff },
  { label: '代课老师', value: PerformanceRoleType.Teacher },
];

function isRatioInputValid(value: string | number | undefined) {
  if (value === '' || value === null || value === undefined) {
    return false;
  }

  const valueString = String(value);
  if (!/^\d+$/.test(valueString)) {
    return false;
  }

  const ratio = Number(valueString);
  return ratio >= 0 && ratio <= 100;
}

function formatCurrency(value: number) {
  const amountInYuan = value / 100;

  return `¥${amountInYuan.toLocaleString('zh-CN', {
    minimumFractionDigits: Number.isInteger(amountInYuan) ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;
}

export default function PerformanceOwnershipSection({
  title,
  description,
  form,
  name = 'allocation',
  storeId,
  totalAmount,
  defaultOwner,
  lockDefaultOwner = true,
}: IProps) {
  const { token } = theme.useToken();
  const ownershipItems = Form.useWatch(name, form) as PerformanceOwnershipFormItem[] | undefined;
  const [loadingMap, setLoadingMap] = useState<Record<PerformanceRoleType, boolean>>({
    [PerformanceRoleType.Staff]: false,
    [PerformanceRoleType.Teacher]: false,
  });
  const [optionsMap, setOptionsMap] = useState<
    Record<PerformanceRoleType, PerformanceOwnerOption[]>
  >({
    [PerformanceRoleType.Staff]: [],
    [PerformanceRoleType.Teacher]: [],
  });

  const ratioTotal = useMemo(
    () => getPerformanceOwnershipRatioTotal(ownershipItems || []),
    [ownershipItems],
  );

  async function requestStaffOptions(nextStoreId?: number): Promise<PerformanceOwnerOption[]> {
    if (!nextStoreId) {
      return [];
    }

    const res = await getStaffList({
      store_id: nextStoreId,
      page_size: 100,
    });

    return (res?.list || []).map((item) => ({
      label: item.username,
      value: item.id,
    }));
  }

  async function requestTeacherOptions(): Promise<PerformanceOwnerOption[]> {
    const res = await getTeacherList({
      page: 1,
      page_size: 100,
      status: EmploymentStatus.EMPLOYED,
    });

    return (res?.list || []).map((item: TeacherInfo) => ({
      label: item.stage_name,
      value: item.id,
    }));
  }

  useEffect(() => {
    if (!lockDefaultOwner) {
      return;
    }

    const nextItems = syncDefaultPerformanceOwnershipItems(form.getFieldValue(name), defaultOwner);
    form.setFieldValue(name, nextItems);
  }, [defaultOwner?.ownerId, defaultOwner?.ownerName, form, lockDefaultOwner, name]);

  useEffect(() => {
    async function loadRoleOptions(
      roleType: PerformanceRoleType,
      loader: (nextStoreId?: number) => Promise<PerformanceOwnerOption[]>,
    ) {
      setLoadingMap((prev) => ({ ...prev, [roleType]: true }));
      try {
        const options = await loader(storeId);
        setOptionsMap((prev) => ({ ...prev, [roleType]: options }));
      } finally {
        setLoadingMap((prev) => ({ ...prev, [roleType]: false }));
      }
    }

    loadRoleOptions(PerformanceRoleType.Staff, requestStaffOptions);
    loadRoleOptions(PerformanceRoleType.Teacher, requestTeacherOptions);
  }, [storeId]);

  useEffect(() => {
    const currentItems = (form.getFieldValue(name) || []) as PerformanceOwnershipFormItem[];
    const nextItems = currentItems.map((item) => {
      if (!item?.allocate_id || !item?.allocate_type) {
        return item;
      }

      const targetOptions = optionsMap[item.allocate_type] || [];
      const matchedOption = targetOptions.find((option) => option.value === item.allocate_id);

      if (!matchedOption) {
        if (item.owner_name || item.from_api || item.allocation_id) {
          return item;
        }

        if (item.is_default && item.allocate_id === defaultOwner?.ownerId) {
          return {
            ...item,
            owner_name: defaultOwner?.ownerName,
          };
        }

        return {
          ...item,
          allocate_id: undefined,
          owner_name: undefined,
        };
      }

      if (item.owner_name !== matchedOption.label) {
        return {
          ...item,
          owner_name: matchedOption.label,
        };
      }

      return item;
    });

    const hasChanged =
      JSON.stringify(currentItems.map((item) => [item?.allocate_id, item?.owner_name])) !==
      JSON.stringify(nextItems.map((item) => [item?.allocate_id, item?.owner_name]));

    if (hasChanged) {
      form.setFieldValue(name, nextItems);
    }
  }, [defaultOwner?.ownerId, defaultOwner?.ownerName, form, name, optionsMap]);

  function updateOwnershipItem(index: number, patch: Partial<PerformanceOwnershipFormItem>) {
    const nextItems = [...((form.getFieldValue(name) || []) as PerformanceOwnershipFormItem[])];
    nextItems[index] = {
      ...nextItems[index],
      ...patch,
    };
    form.setFieldValue(name, nextItems);
  }

  function addOwnershipItem() {
    const nextItems = [...((form.getFieldValue(name) || []) as PerformanceOwnershipFormItem[])];
    nextItems.push({
      allocate_type: PerformanceRoleType.Staff,
      ratio: 0,
      is_default: false,
      from_api: false,
    });
    form.setFieldValue(name, nextItems);
  }

  return (
    <div style={sectionStyle}>
      <Flex align="center" justify="space-between" style={{ marginBottom: 18 }}>
        <div>
          <Typography.Title level={4} style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>
            {title}
          </Typography.Title>
          {description ? (
            <Typography.Paragraph
              type="secondary"
              style={{ marginTop: 8, marginBottom: 0, fontSize: 13 }}
            >
              {description}
            </Typography.Paragraph>
          ) : null}
        </div>
        <Button color="primary" variant="outlined" onClick={() => addOwnershipItem()}>
          新增归属人
        </Button>
      </Flex>

      <Form.List
        name={name}
        rules={[
          {
            validator: async (_, value: PerformanceOwnershipFormItem[]) => {
              const errorMessage = validatePerformanceOwnershipItems(value);
              if (errorMessage) {
                return Promise.reject(new Error(errorMessage));
              }
              return Promise.resolve();
            },
          },
        ]}
      >
        {(fields, { remove }, { errors }) => (
          <>
            {fields.map((field) => {
              const currentItem = (ownershipItems || [])[field.name] || {};
              const isDefault = !!currentItem?.is_default;
              const currentRole = currentItem?.allocate_type || PerformanceRoleType.Staff;
              const baseOwnerOptions = optionsMap[currentRole] || [];
              const ownerOptions =
                currentItem?.allocate_id &&
                currentItem?.owner_name &&
                !baseOwnerOptions.some((option) => option.value === currentItem.allocate_id)
                  ? [
                      {
                        label: currentItem.owner_name,
                        value: currentItem.allocate_id,
                      },
                      ...baseOwnerOptions,
                    ]
                  : baseOwnerOptions;
              const currentAmount =
                currentItem?.from_api && Number.isFinite(Number(currentItem?.amount))
                  ? Number(currentItem.amount)
                  : calculatePerformanceOwnershipAmountInCent(totalAmount, currentItem?.ratio);
              const amountColor = currentAmount < 0 ? token.colorError : '#111827';

              return (
                <div key={field.key} style={{ ...cardStyle, marginBottom: 16 }}>
                  <Form.Item name={[field.name, 'allocation_id']} hidden>
                    <Input />
                  </Form.Item>
                  <Form.Item name={[field.name, 'owner_name']} hidden>
                    <Input />
                  </Form.Item>
                  <Form.Item name={[field.name, 'amount']} hidden>
                    <Input />
                  </Form.Item>
                  <Form.Item name={[field.name, 'from_api']} hidden>
                    <Input />
                  </Form.Item>
                  <Flex gap={16} align="flex-start" wrap={false}>
                    <div>
                      <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                        归属角色
                      </Typography.Text>
                      <Form.Item
                        name={[field.name, 'allocate_type']}
                        style={{ marginTop: 10, marginBottom: 0 }}
                        rules={[{ required: true, message: '请选择归属角色' }]}
                      >
                        <Select
                          placeholder="请选择归属角色"
                          options={roleOptions}
                          disabled={isDefault}
                          style={{ width: 150 }}
                          onChange={(value: PerformanceRoleType) => {
                            updateOwnershipItem(field.name, {
                              allocate_type: value,
                              allocate_id: undefined,
                              owner_name: undefined,
                              from_api: false,
                            });
                          }}
                        />
                      </Form.Item>
                    </div>

                    <div>
                      <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                        归属人
                      </Typography.Text>
                      <Form.Item
                        name={[field.name, 'allocate_id']}
                        style={{ marginTop: 10, marginBottom: 0 }}
                        rules={[{ required: true, message: '请选择归属人' }]}
                      >
                        <Select
                          showSearch
                          placeholder="请选择归属人"
                          options={ownerOptions}
                          loading={loadingMap[currentRole]}
                          disabled={isDefault}
                          style={{ width: 150 }}
                          filterOption={(input, option) =>
                            String(option?.label || '')
                              .toLowerCase()
                              .includes(input.toLowerCase())
                          }
                          onChange={(value) => {
                            const matchedOption = ownerOptions.find(
                              (option) => option.value === value,
                            );
                            updateOwnershipItem(field.name, {
                              allocate_id: value,
                              owner_name: matchedOption?.label,
                              from_api: false,
                            });
                          }}
                        />
                      </Form.Item>
                    </div>

                    <div>
                      <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                        比例
                      </Typography.Text>
                      <Form.Item
                        name={[field.name, 'ratio']}
                        style={{ marginTop: 10, marginBottom: 0 }}
                        validateTrigger={['onBlur']}
                        rules={[
                          {
                            validator: async (_, value) => {
                              if (value === '' || value === null || value === undefined) {
                                return Promise.reject(new Error('请输入比例'));
                              }
                              if (!isRatioInputValid(value)) {
                                return Promise.reject(
                                  new Error('比例需在0-100之间，且只能填写整数'),
                                );
                              }
                              return Promise.resolve();
                            },
                          },
                        ]}
                      >
                        <Input
                          placeholder="请输入"
                          addonAfter="%"
                          style={{ width: 120 }}
                          onChange={(event) => {
                            updateOwnershipItem(field.name, {
                              ratio:
                                event.target.value === '' ? undefined : Number(event.target.value),
                              from_api: false,
                            });
                          }}
                        />
                      </Form.Item>
                    </div>

                    <div style={{ flex: '1 1 0', minWidth: 0 }}>
                      <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                        归属金额
                      </Typography.Text>
                      <div
                        style={{ marginTop: 16, fontSize: 18, fontWeight: 700, color: amountColor }}
                      >
                        {formatCurrency(currentAmount)}
                      </div>
                    </div>

                    <div style={{ flex: '0 0 24px', minWidth: 24, paddingTop: 34 }}>
                      {!isDefault ? (
                        <Button
                          type="text"
                          danger
                          size="small"
                          icon={<DeleteOutlined />}
                          onClick={() => remove(field.name)}
                        />
                      ) : (
                        <div style={{ width: 24, height: 24 }} />
                      )}
                    </div>
                  </Flex>

                  {isDefault && (
                    <Typography.Text
                      type="secondary"
                      style={{ display: 'block', marginTop: 14, fontSize: 13 }}
                    >
                      默认归属人为会员当前负责人，仅支持调整比例。
                    </Typography.Text>
                  )}

                  {!isDefault && (
                    <div style={{ marginTop: 16, width: '90%' }}>
                      <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                        业绩备注
                      </Typography.Text>
                      <Form.Item
                        name={[field.name, 'remark']}
                        style={{ marginTop: 10, marginBottom: 0 }}
                        rules={[
                          {
                            validator: async (_, value) => {
                              if (
                                currentItem?.is_default ||
                                currentItem?.from_api ||
                                value?.trim()
                              ) {
                                return Promise.resolve();
                              }
                              return Promise.reject(new Error('请填写业绩备注'));
                            },
                          },
                        ]}
                      >
                        <Input placeholder="请填写该归属人参与业绩分配的原因" />
                      </Form.Item>
                    </div>
                  )}
                </div>
              );
            })}

            <Form.ErrorList errors={errors} />

            <Flex justify="space-between" align="center" style={summaryStyle}>
              <span>
                比例合计{' '}
                <span style={{ color: '#2f5fd0', fontWeight: 700 }}>
                  {Number.isFinite(ratioTotal) ? ratioTotal : 0}%
                </span>
              </span>
              <span>
                归属金额合计{' '}
                <span style={{ color: '#2f5fd0', fontWeight: 700 }}>
                  {formatCurrency(
                    calculatePerformanceOwnershipAmountInCent(totalAmount, ratioTotal),
                  )}
                </span>
              </span>
            </Flex>

            {fields.length > 1 && (
              <Typography.Text
                style={{
                  display: 'block',
                  marginTop: 14,
                  color: '#cf3f5f',
                  fontSize: 13,
                }}
              >
                已添加额外归属人。每个新增归属人都需要填写备注，说明参与归属的原因。
              </Typography.Text>
            )}
          </>
        )}
      </Form.List>
    </div>
  );
}
